import { FC } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import Styles from './auth.module.scss';
import { resetPassword } from 'aws';
import { Alert } from 'modules/common/components/Alert/Alert';
import { Button } from 'modules/common/components/Button/Button';
import { Input } from 'modules/common/components/Input/Input';
import { Stack } from 'modules/common/components/Stack/Stack';
import { getResetPasswordFormSchema } from './validation-schemas';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import Form from 'modules/common/components/Form/Form';
import useLoading from 'modules/common/hooks/useLoading';

interface ResetPasswordForm {
  code: string;
  password: string;
  confirmPassword: string;
}

export const ResetPassword: FC = () => {
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    isLoading,
    setLoading,
    setError,
    errorMessage: error,
    isError,
  } = useLoading();

  const methods = useForm<ResetPasswordForm>({
    defaultValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onBlur',
    resolver: yupResolver(getResetPasswordFormSchema()),
  });

  const {
    formState: { errors },
    setValue,
  } = methods;

  const onSubmit = async (values: ResetPasswordForm) => {
    try {
      const username = queryParams.get('username');
      if (!username) {
        return navigate('/auth/forgot-password');
      }
      setLoading(true);
      await resetPassword(username, values.code, values.password);
      setLoading(false);
      navigate('/auth/login');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className={Styles['auth-form']}>
      <h4 className="fw-700 font-size-lg ta-center m-b-5">Reset Password</h4>
      {isError && (
        <Alert className="m-b-2" type="error">
          {error}
        </Alert>
      )}
      <Form<ResetPasswordForm> onSubmit={onSubmit} methods={methods}>
        <p className="m-b-1 font-size-sm fw-600">Code</p>
        <Input
          large
          type="text"
          name="code"
          placeholder="Enter confirmation code"
          autoComplete="off"
          onInput={v => setValue('code', v)}
        />
        <p className="m-b-4 font-size-xs color-error">{errors.code?.message}</p>

        <p className="m-b-1 font-size-sm fw-600">Password</p>
        <Input
          large
          type="password"
          name="password"
          placeholder="Enter password"
          autoComplete="off"
          onInput={v => setValue('password', v)}
        />
        <p className="m-b-4 font-size-xs color-error">
          {errors.password?.message}
        </p>

        <p className="m-b-1 font-size-sm fw-600">Confirm Password</p>
        <Input
          large
          type="password"
          name="confirmPassword"
          placeholder="Enter confirm password"
          autoComplete="off"
          onInput={v => setValue('confirmPassword', v)}
        />
        <p className="m-b-4 font-size-xs color-error">
          {errors.confirmPassword?.message}
        </p>

        <Stack justifyContent="space-between" alignItems="center">
          <Stack.Item>
            <Button type="button" secondary>
              <Link to="/auth/login">Cancel</Link>
            </Button>
          </Stack.Item>
          <Stack.Item>
            <Button type="submit" disabled={isLoading}>
              Continue
            </Button>
          </Stack.Item>
        </Stack>
      </Form>
    </div>
  );
};
